import { refreshAccessToken, clearTokens, isAuthenticated } from './authApi';

const INACTIVITY_TIMEOUT = 60 * 60 * 1000; // 1 hour in milliseconds
const CHECK_INTERVAL = 60 * 1000; // Check every minute
const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];

class ActivityTracker {
  private lastActivityTime: number = Date.now();
  private checkInterval: NodeJS.Timeout | null = null;
  private isActive: boolean = false;

  /**
   * Start tracking user activity
   */
  start(): void {
    if (this.isActive) return;

    this.isActive = true;
    this.lastActivityTime = Date.now();

    // Add event listeners for user activity
    ACTIVITY_EVENTS.forEach(event => {
      document.addEventListener(event, this.handleActivity, { passive: true });
    });

    // Start periodic check for inactivity
    this.checkInterval = setInterval(() => {
      this.checkInactivity();
    }, CHECK_INTERVAL);

    console.log('[ActivityTracker] Started monitoring user activity');
  }

  /**
   * Stop tracking user activity
   */
  stop(): void {
    if (!this.isActive) return;

    this.isActive = false;

    // Remove event listeners
    ACTIVITY_EVENTS.forEach(event => {
      document.removeEventListener(event, this.handleActivity);
    });

    // Clear interval
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }

    console.log('[ActivityTracker] Stopped monitoring user activity');
  }

  /**
   * Handle user activity - update last activity time
   */
  private handleActivity = (): void => {
    const now = Date.now();
    const timeSinceLastActivity = now - this.lastActivityTime;

    // Update last activity time
    this.lastActivityTime = now;

    // If user was inactive for more than 5 minutes but less than 1 hour, refresh token
    if (timeSinceLastActivity > 5 * 60 * 1000 && timeSinceLastActivity < INACTIVITY_TIMEOUT) {
      this.refreshTokenSilently();
    }
  };

  /**
   * Check for inactivity and logout if necessary
   */
  private checkInactivity = (): void => {
    if (!isAuthenticated()) {
      this.stop();
      return;
    }

    const now = Date.now();
    const inactiveTime = now - this.lastActivityTime;

    if (inactiveTime >= INACTIVITY_TIMEOUT) {
      console.log('[ActivityTracker] User inactive for 1 hour, logging out...');
      this.handleInactivityLogout();
    }
  };

  /**
   * Silently refresh access token
   */
  private refreshTokenSilently = async (): Promise<void> => {
    try {
      await refreshAccessToken();
      console.log('[ActivityTracker] Token refreshed due to user activity');
    } catch (error) {
      console.error('[ActivityTracker] Failed to refresh token:', error);
    }
  };

  /**
   * Handle logout due to inactivity
   */
  private handleInactivityLogout = (): void => {
    this.stop();
    clearTokens();

    // Store session expiry flag
    sessionStorage.setItem('session_expired', 'true');

    // Redirect to login
    window.location.href = '/login';
  };

  /**
   * Get time since last activity in milliseconds
   */
  getTimeSinceLastActivity(): number {
    return Date.now() - this.lastActivityTime;
  }

  /**
   * Get remaining time before auto-logout in milliseconds
   */
  getRemainingTime(): number {
    const remaining = INACTIVITY_TIMEOUT - this.getTimeSinceLastActivity();
    return Math.max(0, remaining);
  }

  /**
   * Check if currently tracking
   */
  isTracking(): boolean {
    return this.isActive;
  }
}

// Export singleton instance
export const activityTracker = new ActivityTracker();
